// D-043 — the generated app icon. Amended by D-188/D-189/D-190.
//
// There is still no illustrator in this pipeline, so every icon is built from data and the same
// input always yields the same bytes (the icon is cached with the manifest, §9.6, and compared
// against what is on disk). What changed is *which* data, and it is now a ladder:
//
//   1. **What the app declared** — `InProcessApp.icon`, a glyph and optionally a hue (D-188).
//   2. **What the app's own words say** — its `keywords`, or a catalog entry's `category`,
//      matched against the table in `glyphs.ts` (D-189).
//   3. **An abstract mark** — the original hash-picked geometry, which is now what it was
//      always meant to be: the answer for an app nobody has said anything about.
//
// The container is **one silhouette for every icon** (D-190). Picking it from the hash as well
// meant a launcher of six apps was six different outlines carrying six unrelated marks, and a
// set of icons that shares nothing reads as noise however good each one is on its own. Colour
// still varies per app, which is the axis that actually helps you find something.
import type { AppIconSpec, GlyphName } from '@hangar/shared';
import { createDraw, hashSeed } from './seed';
import { GLYPHS, glyphForWords } from './glyphs';

/**
 * The one container silhouette: a squircle, on a 64×64 canvas.
 *
 * A superellipse rather than a rounded rect — it is the shape that reads as "app icon" under
 * all three skins without being any of their trade dress, and it holds a 24×24 Lucide glyph
 * scaled up better than a circle does at the corners.
 */
const CONTAINER =
  'M32 2c11.7 0 18.6 1.2 23.2 5.8S61 20.3 61 32s-1.2 18.6-5.8 23.2S43.7 61 32 61s-18.6-1.2-23.2-5.8S3 43.7 3 32s1.2-18.6 5.8-23.2S20.3 2 32 2Z';

/**
 * Abstract marks, on the same 64×64 canvas, centred. Rung 3 of the ladder — reached only by an
 * app that declares no icon and whose words match nothing. Stroked, never filled.
 */
const ABSTRACT: readonly string[] = [
  'M22 42 32 22l10 20Z', // triangle
  'M22 22h20v20H22Z', // square
  'M32 20v24M20 32h24', // plus
  'M21 43 32 21l11 22M25 36h14', // caret A
  'M22 40c6-16 14-16 20 0', // arch
  'M32 20a12 12 0 1 0 0 24 12 12 0 0 0 0-24M32 27v10', // ringed bar
  'M22 24h20M22 32h14M22 40h20', // lines
  'M23 41 32 23l9 18-9-6Z', // paper plane
  'M24 24l16 16M40 24 24 40', // cross
  'M22 38c5 0 5-12 10-12s5 12 10 12', // wave
  'M32 20 44 32 32 44 20 32Z M32 27l5 5-5 5-5-5Z', // nested diamond
  'M24 42V26l8-6 8 6v16', // house
];

const HUE_SPREAD = 34;

/**
 * A 24×24 Lucide glyph, drawn at 32px and centred on the 64×64 canvas.
 *
 * `(64 - 32) / 2 = 16`, and `32 / 24` is the scale. Lucide's native stroke of 2 comes out at
 * 2.67 after scaling, which sits right against the container's weight — so the stroke width is
 * left alone rather than compensated, and a glyph looks here exactly as Lucide drew it.
 */
const GLYPH_TRANSFORM = 'translate(16 16) scale(1.3333333333333333)';

export interface AppIconOptions {
  /** SVG width/height attribute. The viewBox is always 64×64. */
  size?: number;
  /** Overrides the generated base hue, 0..359. Beaten only by nothing — this is the last word. */
  hue?: number;
  /**
   * Rung 1 — what the app declared (D-188).
   *
   * Explicitly `| undefined` under `exactOptionalPropertyTypes`: the caller looks this up for an
   * app that may have declared nothing and spreads the result, so "absent" and "present and
   * undefined" have to mean the same thing here.
   */
  spec?: AppIconSpec | undefined;
  /** Rung 2 — the app's own words: `keywords`, and a catalog `category` (D-189). */
  words?: readonly string[] | undefined;
}

/** Which rung of the ladder produced this icon. The Inspector shows it; tests assert on it. */
export type AppIconSource = 'declared' | 'keywords' | 'abstract';

export interface GeneratedAppIcon {
  svg: string;
  hue: number;
  source: AppIconSource;
  /** Set on rungs 1 and 2; `null` for an abstract mark. */
  glyph: GlyphName | null;
  /** Set only for an abstract mark; `null` otherwise. */
  abstractIndex: number | null;
}

/** A stable, collision-resistant gradient id so two icons can coexist in one document. */
function gradientId(seed: number): string {
  return `hg-${seed.toString(36)}`;
}

export function generateAppIconParts(
  appId: string,
  options: AppIconOptions = {},
): GeneratedAppIcon {
  const seed = hashSeed(appId);
  const draw = createDraw(seed);

  // Drawn unconditionally, before any branch: the abstract index has to come off the sequence
  // in the same position whatever rung wins, or an app would change its fallback mark simply
  // by declaring an icon — and the fallback is what it wears again if the declaration is ever
  // removed. Determinism is a property of the id, not of the call site.
  const abstractIndex = draw.next() % ABSTRACT.length;
  const derivedHue = draw.next() % 360;

  const declared = options.spec;
  const fromWords = declared ? null : glyphForWords(options.words ?? []);
  const glyph: GlyphName | null = declared?.glyph ?? fromWords;
  const source: AppIconSource = declared ? 'declared' : fromWords ? 'keywords' : 'abstract';

  const hue = options.hue ?? declared?.hue ?? derivedHue;
  const hue2 = (hue + HUE_SPREAD) % 360;
  const size = options.size ?? 64;
  const id = gradientId(seed);
  const stroke = `hsl(${hue} 40% 96%)`;

  const mark =
    glyph === null
      ? `<path d="${ABSTRACT[abstractIndex] ?? ABSTRACT[0]}" fill="none" stroke="${stroke}" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>`
      : `<g transform="${GLYPH_TRANSFORM}" fill="none" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${GLYPHS[glyph]}</g>`;

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="${size}" height="${size}" role="img" aria-label="${escapeAttr(appId)}">` +
    `<defs><linearGradient id="${id}" x1="0" y1="0" x2="1" y2="1">` +
    `<stop offset="0" stop-color="hsl(${hue} 62% 56%)"/>` +
    `<stop offset="1" stop-color="hsl(${hue2} 58% 38%)"/>` +
    `</linearGradient></defs>` +
    `<path d="${CONTAINER}" fill="url(#${id})"/>` +
    mark +
    `</svg>`;

  return {
    svg,
    hue,
    source,
    glyph,
    abstractIndex: glyph === null ? abstractIndex : null,
  };
}

export function generateAppIcon(appId: string, options: AppIconOptions = {}): string {
  return generateAppIconParts(appId, options).svg;
}

/** For `<img src>` and CSS `url()`. Deterministic for the same input, like the SVG itself. */
export function appIconDataUri(appId: string, options: AppIconOptions = {}): string {
  return `data:image/svg+xml,${encodeURIComponent(generateAppIcon(appId, options))}`;
}

function escapeAttr(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

/** Exposed for the generator's own tests; not part of the public art surface. */
export const APPICON_ABSTRACT_COUNT = ABSTRACT.length;
